import { useTranslation } from 'react-i18next';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Flex,
  Skeleton,
  Text,
  cn,
} from '@kognitos/lattice';
import { ValueChip } from '@/modules/outputs/components/ValueChip';
import {
  getInitials,
  computeTimestampDuration,
  formatProcedureLabel,
} from '@/shared/utils';
import type { IResolvedExternalCall } from '../../utils/external-call-visibility';
import { invocationKey } from '@/modules/automation-run/hooks/useRunJournalEvents';
import { useBookIdentity } from '../../hooks/useBookIdentity';
import { TimelineBar } from './TimelineBar';

/** Book icon and display name for one external call. */
function BookBadge({ bookName }: { bookName?: string }) {
  const { icon, displayName, isLoading } = useBookIdentity(bookName);

  if (isLoading) {
    return <Skeleton className="h-4 w-20 rounded" />;
  }

  const label = displayName || bookName || '';

  return (
    <Flex align="center" gap={1} className="min-w-0">
      <Avatar className="size-4 shrink-0 rounded">
        {icon && (
          <AvatarImage src={icon} alt={label} className="object-contain" />
        )}
        <AvatarFallback className="rounded bg-muted text-[8px] font-medium text-muted-foreground">
          {getInitials(label)}
        </AvatarFallback>
      </Avatar>
      <Text level="xSmall" color="muted" className="truncate">
        {label}
      </Text>
    </Flex>
  );
}

/** One external call row: procedure, book, duration and its result chip. */
function ExternalCallRow({
  call,
  output,
  isLast,
}: IResolvedExternalCall & { isLast: boolean }) {
  const { t } = useTranslation('automation');
  const duration = computeTimestampDuration(
    call.startedTimestamp,
    call.timestamp,
  );
  const rawProcedure = call.bookFunction?.functionName ?? call.functionName;
  const procedureLabel = formatProcedureLabel(rawProcedure, t);

  return (
    <Flex gap={2} className="w-full">
      <TimelineBar iconType="Blocks" hideBottomLine={isLast} />
      <Flex
        direction="column"
        gap={1}
        flex="1"
        className={cn('min-w-0', !isLast && 'pb-2')}
      >
        <Flex align="center" gap={2} className="w-full min-w-0">
          <Text
            level="small"
            title={rawProcedure}
            className="min-w-0 flex-1 truncate"
          >
            {procedureLabel}
          </Text>
          {duration && (
            <Text level="xSmall" color="muted" className="shrink-0">
              {duration}
            </Text>
          )}
        </Flex>
        <BookBadge bookName={call.bookFunction?.bookName} />
        {/* Chip, not an inline renderer: the panel is a fifth of the viewport
            and the layover behind the chip hosts the full-size widget. */}
        {output != null && (
          <Flex className="w-full pt-0.5">
            <ValueChip
              name={t('automation.runLog.externalCallResult')}
              value={output}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

/**
 * Props for the ExternalCallsSection component.
 */
interface IExternalCallsSectionProps {
  /** Every call the step made, in execution order, with its output. */
  calls: IResolvedExternalCall[];
  /** Heading styling, to sit level with the host's own section labels. */
  labelTone?: 'prominent' | 'muted';
}

/**
 * The external book calls a step made, rendered inside that step's card.
 *
 * A step function drops the payload of the calls it makes (an IDP extraction
 * returns a rich result, but the step returns a scalar), so this is the only
 * place that payload is reachable. One row per call, its result under it.
 *
 * @param {IExternalCallsSectionProps} props - Component props
 * @returns {JSX.Element | null} The section, or null when there is nothing to show
 */
export const ExternalCallsSection = ({
  calls,
  labelTone = 'prominent',
}: IExternalCallsSectionProps) => {
  const { t } = useTranslation('automation');

  if (calls.length === 0) {
    return null;
  }

  const isMutedLabel = labelTone === 'muted';

  return (
    <Flex
      direction="column"
      gap={2}
      className="w-full"
      data-testid="run-log-external-calls"
    >
      <Text
        level={isMutedLabel ? 'xSmall' : 'small'}
        color={isMutedLabel ? 'muted' : undefined}
        weight={isMutedLabel ? undefined : 'medium'}
      >
        {t('automation.runLog.externalActions')}
      </Text>

      {calls.map((entry, index) => (
        <ExternalCallRow
          key={invocationKey(entry.call)}
          call={entry.call}
          output={entry.output}
          isLast={index === calls.length - 1}
        />
      ))}
    </Flex>
  );
};
